import React from 'react';
import { CardMedia, Chip, Grid, Typography, useMediaQuery, useTheme } from '@mui/material';
import { Groups, Person, School } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { OrderPageData, OrderPageTypes } from '../../../api';
import { ORDER_PAGE_TYPE_COLORS } from './orderPageTypeColors';
import { useLanguage } from '../../../utils/GeneralUtils';
import { useAccountData } from '../../StorefrontThemeWrapper';
import { getPrimaryPhoto } from '../../../utils/orderPageImageUtils';
import { useStartingPriceLabel } from '../../common/storefrontPriceUtils';
import { AccountLogo } from '../../../shared/ui/AccountLogo';

interface ExperienceCardProps {
  orderPage: OrderPageData;
}

export const ExperienceCard = ({ orderPage }: ExperienceCardProps) => {
  const { accountId, storefrontId } = useParams();
  const [searchParams] = useSearchParams();
  const account = useAccountData();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();
  const language = useLanguage();

  const { label: priceLabel, startingPrice } = useStartingPriceLabel(orderPage, account);

  return (
    <Link
      to={{
        pathname: `/experience/${accountId}/${orderPage.id}/${storefrontId || ''}`,
        search: searchParams.toString(),
      }}
      style={{ textDecoration: 'none', color: 'inherit', height: '100%', display: 'block' }}>
      <Grid
        container
        flexDirection="column"
        sx={{
          height: '100%',
          borderRadius: 2,
          overflow: 'hidden',
          border: '1px solid #e0e0e0',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          transition: 'transform 0.2s, box-shadow 0.2s',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
          },
        }}>
        <Grid
          sx={{
            position: 'relative',
            height: isSmallScreen ? 200 : 300,
            backgroundColor: getPrimaryPhoto(orderPage) ? 'transparent' : '#f5f5f5',
          }}>
          {getPrimaryPhoto(orderPage) ? (
            <CardMedia
              component="img"
              height={isSmallScreen ? 200 : 300}
              image={getPrimaryPhoto(orderPage, isSmallScreen ? 400 : 600)}
              alt={orderPage.label?.[language] || ''}
              sx={{ objectFit: 'cover' }}
            />
          ) : (
            <Grid container justifyContent="center" alignItems="center" height="100%">
              {account?.logo ? (
                <AccountLogo
                  account={account}
                  width={200}
                  style={{ maxHeight: '60%', maxWidth: '60%', objectFit: 'contain' }}
                />
              ) : (
                <Typography variant="h2" sx={{ opacity: 0.3 }}>
                  🎨
                </Typography>
              )}
            </Grid>
          )}
          <Chip
            icon={
              orderPage.order_page_type === OrderPageTypes.PRIVATE ? (
                <Person />
              ) : orderPage.order_page_type === OrderPageTypes.COURSE ? (
                <School />
              ) : (
                <Groups />
              )
            }
            label={
              orderPage.order_page_type === OrderPageTypes.PRIVATE
                ? t('storefront.filterPrivate')
                : orderPage.order_page_type === OrderPageTypes.COURSE
                ? t('storefront.filterCourse')
                : t('storefront.filterJoinGroup')
            }
            size="small"
            sx={{
              position: 'absolute',
              top: 12,
              left: 12,
              backgroundColor: ORDER_PAGE_TYPE_COLORS[orderPage.order_page_type] || '#2B334D',
              color: 'white',
              backdropFilter: 'blur(8px)',
              fontWeight: 500,
              '& .MuiChip-icon': {
                color: 'white',
                fontSize: 16,
              },
            }}
          />
        </Grid>
        <Grid container flexDirection="column" p={2} flex={1}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              marginBottom: 1,
              lineHeight: 1.3,
              display: '-webkit-box',
              WebkitLineClamp: 1,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}>
            {orderPage.label?.[language]}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              marginBottom: 1,
              minHeight: '2.8em',
              lineHeight: 1.4,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
            dangerouslySetInnerHTML={{ __html: orderPage.description?.[language] || '' }}
          />
          <Grid container mt="auto">
            {/* hp param: hides prices — customer conversion test */}
            {startingPrice > 0 && !searchParams.has('hp') && (
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                {priceLabel}
              </Typography>
            )}
          </Grid>
        </Grid>
      </Grid>
    </Link>
  );
};
